import { useEffect, useState, useCallback } from "react";
import { Routes, Route, Link, NavLink, useNavigate, useParams, Navigate, useLocation } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, ShieldCheck, LogOut, Lock, Users, BarChart3, Eye, FileSearch,
  Search, Copy, TrendingUp, AlertTriangle, KeyRound, Wallet, Pencil, X, Download, Bell, Smartphone,
  ShieldAlert,
} from "lucide-react";
import FoxLogo from "@/components/FoxLogo";

// ===================================================================
// 2FA Gate — every admin route is wrapped in this
// ===================================================================
export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null); // {is_admin, enrolled, verified_in_session}
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/2fa/status").then(r => setStatus(r.data)).catch(err => {
      if (err?.response?.status === 401) navigate("/login?next=" + encodeURIComponent(location.pathname));
      else if (err?.response?.status === 403) setError("Account non autorizzato — solo super-admin.");
      else setError(err?.response?.data?.detail || "Errore di autenticazione");
    });
  }, [navigate, location.pathname]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-stone-900 p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-700 mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-2">Accesso negato</h1>
          <p className="text-stone-600 mb-6">{error}</p>
          <Link to="/app" className="underline text-blue-700" data-testid="admin-back-link">Torna all'app</Link>
        </div>
      </div>
    );
  }

  if (!status) return <div className="min-h-screen flex items-center justify-center text-stone-500">…</div>;
  if (!status.enrolled) return <Enroll2FA onDone={() => setStatus({ ...status, enrolled: true })} />;
  if (!status.verified_in_session) return <Verify2FA onDone={() => setStatus({ ...status, verified_in_session: true })} />;

  return (
    <div className="min-h-screen bg-white text-stone-900">
      <header className="bg-stone-900 text-stone-50 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-6">
          <Link to="/admin" className="flex items-center gap-2.5 font-serif text-lg">
            <FoxLogo className="w-6 h-6" />
            <span>Xenia<span className="text-blue-500">.</span></span>
            <span className="text-xs tracked text-blue-500 ml-1">ADMIN</span>
          </Link>
          <nav className="flex items-center gap-1 ml-6 text-sm">
            <Tab to="/admin"           icon={BarChart3}  testid="admin-tab-dashboard">Operazioni</Tab>
            <Tab to="/admin/alerts"    icon={Bell}       testid="admin-tab-alerts">Allerte</Tab>
            <Tab to="/admin/users"     icon={Users}      testid="admin-tab-users">Clienti</Tab>
            <Tab to="/admin/finance"   icon={Wallet}     testid="admin-tab-finance">Finanza</Tab>
            <Tab to="/admin/stats"     icon={TrendingUp} testid="admin-tab-stats">Statistiche</Tab>
            <Tab to="/admin/security"  icon={ShieldAlert} testid="admin-tab-security">Sicurezza</Tab>
            <Tab to="/admin/audit"     icon={FileSearch} testid="admin-tab-audit">Audit log</Tab>
          </nav>
          <button onClick={async () => {
              await api.post("/admin/2fa/lock");
              setStatus({ ...status, verified_in_session: false });
            }}
            className="ml-auto inline-flex items-center gap-2 text-xs text-stone-400 hover:text-stone-50" data-testid="admin-lock">
            <Lock className="w-3.5 h-3.5" /> Blocca sessione
          </button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Routes>
          <Route index element={<Operations />} />
          <Route path="alerts" element={<Alerts />} />
          <Route path="users" element={<UsersList />} />
          <Route path="users/:id" element={<ClientDetail />} />
          <Route path="finance" element={<Finance />} />
          <Route path="stats" element={<StatsOverview />} />
          <Route path="security" element={<SecurityPanel />} />
          <Route path="audit" element={<AuditLog />} />
        </Routes>
      </main>
    </div>
  );
}

function Tab({ to, icon: Icon, children, testid }) {
  return (
    <NavLink to={to} end={to === "/admin"}
      data-testid={testid}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm transition-colors ${
          isActive ? "bg-stone-800 text-stone-50" : "text-stone-400 hover:text-stone-50 hover:bg-stone-800/50"
        }`}>
      <Icon className="w-4 h-4" /> {children}
    </NavLink>
  );
}

// ===================================================================
// 2FA: enroll
// ===================================================================
function Enroll2FA({ onDone }) {
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.post("/admin/2fa/enroll").then(r => setEnroll(r.data)).catch(() => {}); }, []);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/2fa/verify", { code });
      toast.success("2FA attivata");
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Codice non valido"); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Attiva 2FA" subtitle="Una volta sola: scansiona il QR con Google Authenticator / Authy / 1Password, poi inserisci il codice per confermare.">
      {enroll ? (
        <div className="space-y-5" data-testid="admin-2fa-enroll">
          <div className="flex justify-center bg-white border border-stone-300 p-4 rounded-sm">
            <img src={`${API}/admin/2fa/qr.png`} alt="QR 2FA" className="w-48 h-48" />
          </div>
          <details className="text-xs text-stone-500">
            <summary className="cursor-pointer">Inserisci il codice manualmente</summary>
            <code className="block mt-2 bg-stone-100 p-2 font-mono break-all">{enroll.secret}</code>
          </details>
          <form onSubmit={verify} className="space-y-3">
            <label className="block">
              <span className="text-xs tracked text-stone-500 block mb-1">CODICE A 6 CIFRE</span>
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" pattern="[0-9]{6}" autoFocus
                placeholder="000000"
                className="w-full border border-stone-300 px-3 py-2.5 text-center text-2xl font-mono tracking-widest focus:outline-none focus:border-blue-700"
                data-testid="admin-2fa-code" />
            </label>
            <button type="submit" disabled={busy || code.length !== 6}
              className="w-full bg-stone-900 text-stone-50 py-2.5 text-sm rounded-sm hover:bg-stone-800 disabled:opacity-50" data-testid="admin-2fa-verify">
              {busy ? "Verifico…" : "Attiva 2FA"}
            </button>
          </form>
        </div>
      ) : (
        <p className="text-stone-500 text-sm">Generazione QR…</p>
      )}
    </AuthShell>
  );
}

function Verify2FA({ onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/2fa/verify", { code });
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Codice non valido"); }
    finally { setBusy(false); }
  };
  const doReset = async (e) => {
    e.preventDefault();
    if (!window.confirm("Azzerare il 2FA? Dopo dovrai rifare l'enrollment scansionando un nuovo QR code.")) return;
    setResetBusy(true);
    try {
      await api.post("/admin/2fa/reset", { password: resetPw });
      toast.success("2FA azzerato. Ti porto alla schermata di setup nuovo QR…");
      window.location.reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset fallito"); }
    finally { setResetBusy(false); }
  };
  return (
    <AuthShell title="Verifica 2FA" subtitle="Inserisci il codice a 6 cifre dalla tua app Authenticator.">
      <form onSubmit={submit} className="space-y-3" data-testid="admin-2fa-prompt">
        <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric" autoFocus placeholder="000000"
          className="w-full border border-stone-300 px-3 py-3 text-center text-2xl font-mono tracking-widest"
          data-testid="admin-2fa-verify-input" />
        <button type="submit" disabled={busy || code.length !== 6}
          className="w-full bg-stone-900 text-stone-50 py-2.5 text-sm rounded-sm hover:bg-stone-800 disabled:opacity-50" data-testid="admin-2fa-verify-btn">
          {busy ? "Verifico…" : "Entra"}
        </button>
      </form>
      <div className="mt-6 pt-4 border-t border-stone-100">
        {!showReset ? (
          <button onClick={() => setShowReset(true)} className="text-xs text-stone-500 hover:text-blue-700 underline" data-testid="admin-2fa-reset-toggle">
            Ho perso accesso all'app Authenticator — azzera 2FA
          </button>
        ) : (
          <form onSubmit={doReset} className="space-y-3" data-testid="admin-2fa-reset-form">
            <p className="text-xs text-stone-600">Per resettare il 2FA inserisci la tua password admin. Dopo il reset scansionerai un nuovo QR.</p>
            <input type="password" value={resetPw} onChange={e => setResetPw(e.target.value)} required minLength={4}
              placeholder="Password admin"
              className="w-full border border-stone-300 px-3 py-2 text-sm"
              data-testid="admin-2fa-reset-password" />
            <div className="flex gap-2">
              <button type="submit" disabled={resetBusy} className="bg-red-700 text-white px-3 py-2 text-xs rounded-sm" data-testid="admin-2fa-reset-confirm">
                {resetBusy ? "Azzero…" : "Azzera 2FA"}
              </button>
              <button type="button" onClick={() => setShowReset(false)} className="text-xs text-stone-500 px-2">Annulla</button>
            </div>
          </form>
        )}
      </div>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <div className="max-w-md w-full bg-white border border-stone-200 p-8 rounded-sm">
        <div className="flex items-center gap-2 text-blue-700 mb-2"><ShieldCheck className="w-5 h-5" /><span className="text-xs tracked">XENIA · ADMIN</span></div>
        <h1 className="font-serif text-3xl mb-2">{title}</h1>
        <p className="text-stone-600 text-sm mb-6">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

// ===================================================================
// OPERATIONS — Dashboard operativa cross-host (iter 25)
// ===================================================================
function Operations() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/operations").then(r => setData(r.data)).catch(err => toast.error(err?.response?.data?.detail || "Errore")); }, []);
  if (!data) return <p className="text-stone-500">…</p>;
  const m = data.mrr, l = data.last_30_days;
  return (
    <div data-testid="admin-operations">
      <p className="text-xs tracked text-blue-700 mb-2">PANNELLO PROPRIETARIO</p>
      <h1 className="font-serif text-4xl mb-2">Operazioni Xenia</h1>
      <p className="text-stone-600 mb-8">Vista cross-host: ricavi, attività e tassa di soggiorno raccolta da tutti i tuoi clienti.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Kpi label="MRR STIMATO" value={`€${m.total_estimate.toLocaleString("it-IT")}`} hint={`€${m.subscription} fisso · €${m.metered_last_month} variabile`} />
        <Kpi label="STRUTTURE TOTALI" value={m.properties_total} hint="totale gestite" />
        <Kpi label="NOTTI ULTIMI 30G" value={l.nights.toLocaleString("it-IT")} hint={`${l.bookings_count} prenotazioni`} />
        <Kpi label="FATTURATO 30G" value={`€${l.revenue_total.toLocaleString("it-IT")}`} hint={`Tassa raccolta €${l.tourist_tax_collected}`} />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <section className="border border-stone-200 bg-white p-5">
          <h2 className="font-serif text-2xl mb-3">Top clienti per fatturato</h2>
          <table className="w-full text-sm" data-testid="admin-top-revenue">
            <thead className="text-xs tracked text-stone-500">
              <tr><th className="text-left py-1">CLIENTE</th><th className="text-right py-1">PRENOT.</th><th className="text-right py-1">FATTURATO</th></tr>
            </thead>
            <tbody>
              {data.top_clients_by_revenue.map(c => (
                <tr key={c.id} className="border-t border-stone-100">
                  <td className="py-1">
                    <Link to={`/admin/users/${c.id}`} className="text-blue-700 hover:underline">{c.full_name || c.email}</Link>
                    <div className="text-xs text-stone-400 font-mono">{c.email}</div>
                  </td>
                  <td className="py-1 text-right">{c.bookings}</td>
                  <td className="py-1 text-right font-medium">€{c.revenue.toLocaleString("it-IT")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="border border-stone-200 bg-white p-5">
          <h2 className="font-serif text-2xl mb-3">Tassa di soggiorno per struttura (30g)</h2>
          {data.tax_by_property_top.length === 0
            ? <p className="text-stone-500 text-sm">Nessuna tassa registrata negli ultimi 30 giorni.</p>
            : <table className="w-full text-sm" data-testid="admin-tax-by-prop">
                <thead className="text-xs tracked text-stone-500"><tr><th className="text-left py-1">STRUTTURA</th><th className="text-left py-1">COMUNE</th><th className="text-right py-1">RACCOLTO</th></tr></thead>
                <tbody>
                  {data.tax_by_property_top.map((p, i) => (
                    <tr key={`tx-${i}`} className="border-t border-stone-100">
                      <td className="py-1">{p.name}</td>
                      <td className="py-1 text-xs text-stone-500">{p.comune || "—"}</td>
                      <td className="py-1 text-right font-medium">€{p.tax_total.toLocaleString("it-IT")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </section>
      </div>

      <section className="border border-stone-200 bg-white p-5 mb-8">
        <h2 className="font-serif text-2xl mb-3">Tipologia strutture</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          {data.property_types.map(t => (
            <span key={t.type} className="px-3 py-1 border border-stone-200 bg-stone-50">
              <strong className="font-medium">{t.type}</strong> <span className="text-stone-500">· {t.count}</span>
            </span>
          ))}
        </div>
      </section>

      <DemoSeed />
      <DangerZone />
    </div>
  );
}

function DemoSeed() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const seed = async () => {
    if (!window.confirm("Crea un host demo 'demo@xeniaggr.com' con 1 struttura, 3 camere, 3 ospiti, 5 prenotazioni? Se l'account esiste già viene ricreato.")) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/danger/seed-demo", {});
      setResult(res.data);
      toast.success("Host demo creato — credenziali nel riquadro qui sotto");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <section className="border-2 border-blue-300 bg-blue-50/40 p-5 mb-5" data-testid="demo-seed">
      <h2 className="font-serif text-2xl text-blue-900 flex items-center gap-2"><Smartphone className="w-5 h-5" /> Host demo per test</h2>
      <p className="text-sm text-stone-700 mt-2 mb-4">Crea un host "Hotel Demo Roma" con dati realistici: 3 camere, 3 ospiti (IT/FR/DE), 5 prenotazioni (passate, attuali, future). Utile per provare l'app come se fosse un cliente vero, o per fare demo a investitori.</p>
      {!result ? (
        <button onClick={seed} disabled={busy}
          className="bg-blue-800 text-white px-4 py-2 text-sm rounded-sm hover:bg-blue-700 disabled:opacity-50"
          data-testid="demo-seed-btn">
          {busy ? "Creo…" : "Crea host demo"}
        </button>
      ) : (
        <div className="bg-white border border-blue-200 p-4 rounded-sm space-y-2 text-sm">
          <p className="font-medium">✓ Demo pronto</p>
          <p className="font-mono text-xs">📧 {result.credentials.email}</p>
          <p className="font-mono text-xs">🔑 {result.credentials.password}</p>
          <p className="text-stone-600 text-xs">Strutture: 1 · Camere: {result.created.rooms} · Ospiti: {result.created.guests} · Prenotazioni: {result.created.bookings}</p>
          <a href="/login" target="_blank" rel="noreferrer" className="inline-block text-blue-700 underline text-xs">→ Apri /login in nuova scheda</a>
          <button onClick={() => setResult(null)} className="ml-3 text-stone-500 text-xs underline">Nascondi</button>
        </div>
      )}
    </section>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const wipe = async (e) => {
    e.preventDefault();
    if (!window.confirm("ATTENZIONE: cancelli TUTTI gli host e i loro dati (strutture, ospiti, prenotazioni, pagamenti). L'account admin viene mantenuto. Procedere?")) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/danger/wipe-clients", { password: pw, reason });
      const totals = Object.entries(res.data.deleted).map(([k, v]) => `${k}: ${v}`).join("\n");
      toast.success("Reset completato — dati eliminati:\n" + totals, { duration: 8000 });
      setOpen(false); setPw(""); setReason("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <section className="border-2 border-red-300 bg-red-50/40 p-5" data-testid="danger-zone">
      <h2 className="font-serif text-2xl text-red-800 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Danger Zone</h2>
      <p className="text-sm text-stone-700 mt-2 mb-4">Azione irreversibile: <strong>cancella tutti gli host registrati</strong> (utenti, strutture, camere, ospiti, prenotazioni, pagamenti, notifiche). L'account admin e i log di audit restano integri. Da usare solo per resettare l'ambiente prima di una nuova fase di test reali.</p>
      {!open ? (
        <button onClick={() => setOpen(true)} className="bg-red-700 text-white px-4 py-2 text-sm rounded-sm hover:bg-red-600" data-testid="danger-open-btn">
          Azzera tutti i clienti
        </button>
      ) : (
        <form onSubmit={wipe} className="space-y-3 max-w-md" data-testid="danger-form">
          <FormRow label="Password admin (conferma)">
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} required minLength={4}
              className={inp} data-testid="danger-password" autoFocus />
          </FormRow>
          <FormRow label="Motivo (audit log)">
            <input value={reason} onChange={e => setReason(e.target.value)} required minLength={3}
              placeholder="Es. 'Reset pre-test reale 13/02/2026'"
              className={inp} data-testid="danger-reason" />
          </FormRow>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="bg-red-700 text-white px-4 py-2 text-sm rounded-sm disabled:opacity-50" data-testid="danger-confirm">
              {busy ? "Cancello…" : "Sì, cancella TUTTO"}
            </button>
            <button type="button" onClick={() => { setOpen(false); setPw(""); setReason(""); }} className="text-stone-600 px-3 py-2 text-sm">Annulla</button>
          </div>
        </form>
      )}
    </section>
  );
}

function Kpi({ label, value, hint }) {
  return (
    <div className="border border-stone-200 bg-white p-5">
      <div className="text-xs tracked text-stone-500 mb-1">{label}</div>
      <div className="font-serif text-3xl">{value}</div>
      {hint && <div className="text-xs text-stone-400 mt-1">{hint}</div>}
    </div>
  );
}

// ===================================================================
// CLIENTI — Lista arricchita con filtri (iter 25)
// ===================================================================
function planBadgeStyle(u) {
  if (u.blocked) return "bg-red-50 text-red-700 border-red-200";
  if (u.subscription_status === "active") return "bg-green-50 text-green-800 border-green-200";
  return "bg-stone-50 text-stone-600 border-stone-200";
}

function UsersList() {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [city, setCity] = useState("");
  const [sort, setSort] = useState("created_at");
  const [page, setPage] = useState(0);
  const limit = 25;
  const load = useCallback(async () => {
    const params = new URLSearchParams({ q, sort, limit: String(limit), skip: String(page * limit) });
    if (status) params.set("status", status);
    if (city) params.set("city", city);
    const r = await api.get(`/admin/clients?${params.toString()}`);
    setData(r.data);
  }, [q, status, city, sort, page]);
  useEffect(() => { load().catch(err => toast.error(err?.response?.data?.detail || "Errore")); }, [load]);

  return (
    <div data-testid="admin-users">
      <h1 className="font-serif text-4xl mb-2">Clienti</h1>
      <p className="text-stone-600 mb-6">{data.total} clienti host registrati. Cerca per email, nome o codice cliente.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(0); }}
            placeholder="Email, nome o codice…"
            className="w-full pl-9 pr-3 py-2 border border-stone-300 bg-white text-sm focus:outline-none focus:border-blue-700"
            data-testid="admin-users-search" />
        </div>
        <input value={city} onChange={e => { setCity(e.target.value); setPage(0); }}
          placeholder="Filtra per città…"
          className="px-3 py-2 border border-stone-300 bg-white text-sm focus:outline-none focus:border-blue-700"
          data-testid="admin-users-city" />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(0); }}
          className="px-3 py-2 border border-stone-300 bg-white text-sm" data-testid="admin-users-status">
          <option value="">Tutti gli stati</option>
          <option value="trialing">In prova</option>
          <option value="active">Attivi</option>
          <option value="past_due">Pagamento scaduto</option>
          <option value="blocked">Bloccati</option>
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)}
          className="px-3 py-2 border border-stone-300 bg-white text-sm" data-testid="admin-users-sort">
          <option value="created_at">Più recenti</option>
          <option value="revenue">Fatturato ↓</option>
          <option value="bookings">Prenotazioni ↓</option>
          <option value="guests">Ospiti ↓</option>
        </select>
      </div>

      <div className="border border-stone-200 bg-white overflow-x-auto" data-testid="admin-users-table">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs tracked text-stone-500">
            <tr>
              <th className="text-left px-3 py-2">CLIENTE</th>
              <th className="text-left px-3 py-2">STRUTTURE</th>
              <th className="text-right px-3 py-2">PRENOT.</th>
              <th className="text-right px-3 py-2">OSPITI</th>
              <th className="text-right px-3 py-2">FATTURATO</th>
              <th className="text-right px-3 py-2">TASSA SOGG.</th>
              <th className="text-left px-3 py-2">PIANO</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(u => (
              <tr key={u.id} className="border-t border-stone-100 hover:bg-stone-50">
                <td className="px-3 py-2">
                  <div className="font-medium">{u.full_name || "—"}</div>
                  <div className="text-xs font-mono text-stone-500">{u.email}</div>
                  <div className="text-[10px] font-mono text-stone-400">ID: {u.id.slice(0, 8)}…</div>
                </td>
                <td className="px-3 py-2 text-xs text-stone-700">
                  {(u.properties || []).map(p => p.name).join(", ") || "—"}
                  {u.properties_count > 0 && <span className="text-stone-400"> ({u.properties_count})</span>}
                </td>
                <td className="px-3 py-2 text-right">{u.bookings_count}</td>
                <td className="px-3 py-2 text-right">{u.guests_count}</td>
                <td className="px-3 py-2 text-right font-medium">€{(u.revenue_total || 0).toLocaleString("it-IT")}</td>
                <td className="px-3 py-2 text-right text-xs">€{(u.tourist_tax_total || 0).toLocaleString("it-IT")}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 text-[10px] rounded-sm border ${planBadgeStyle(u)}`}>
                    {u.blocked ? "blocked" : u.subscription_status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/admin/users/${u.id}`} className="text-blue-700 underline text-xs" data-testid={`admin-user-link-${u.id}`}>apri →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm">
        <span className="text-stone-500">Pagina {page + 1} di {Math.max(1, Math.ceil(data.total / limit))} · {data.total} clienti</span>
        <div className="flex gap-2">
          <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}
            className="border border-stone-300 px-3 py-1 disabled:opacity-50">‹ Indietro</button>
          <button disabled={(page + 1) * limit >= data.total} onClick={() => setPage(p => p + 1)}
            className="border border-stone-300 px-3 py-1 disabled:opacity-50">Avanti ›</button>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// CLIENT DETAIL — completo con editing ospite/prenotazione (iter 25)
// ===================================================================
function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview"); // overview|guests|bookings|payments
  const [editingGuest, setEditingGuest] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editingUser, setEditingUser] = useState(false);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [reason, setReason] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get(`/admin/clients/${id}/full`).then(r => setData(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || "Errore"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-stone-500">…</p>;
  const u = data.user, k = data.kpi;

  const impersonate = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Stai per accedere come ${u.email}. L'azione viene loggata. Continuare?`)) return;
    try {
      await api.post(`/admin/users/${id}/impersonate`, { reason });
      toast.success("Impersonation attivata · 4 ore");
      navigate("/app");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const guestRows = data.guests;
  const bookingRows = data.bookings;

  return (
    <div data-testid="admin-client-detail">
      <Link to="/admin/users" className="text-blue-700 text-sm underline">← Clienti</Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1">{u.full_name || u.email}</h1>
          <p className="text-stone-500 font-mono text-sm">{u.email} {u.phone && <span>· {u.phone}</span>}</p>
          <p className="text-[10px] font-mono text-stone-400 mt-1">CODE: {u.id}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditingUser(true)} className="inline-flex items-center gap-1.5 border border-stone-300 px-3 py-1.5 text-sm hover:bg-stone-100" data-testid="admin-client-edit-btn">
            <Pencil className="w-3.5 h-3.5" /> Modifica anagrafica
          </button>
          {(u.billing?.blocked || u.billing?.subscription_status === "past_due") && (
            <button onClick={async () => {
              if (!window.confirm("Riattivare questo account host con un nuovo trial di 14 giorni?")) return;
              try {
                await api.post(`/admin/clients/${id}/unblock`);
                toast.success("Account riattivato · nuovo trial 14 giorni");
                load();
              } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
            }} className="inline-flex items-center gap-1.5 bg-green-700 text-white px-3 py-1.5 text-sm hover:bg-green-600" data-testid="admin-unblock-btn">
              <ShieldCheck className="w-3.5 h-3.5" /> Riattiva account
            </button>
          )}
          <button onClick={() => setShowImpersonate(true)} className="inline-flex items-center gap-1.5 bg-blue-700 text-white px-3 py-1.5 text-sm hover:bg-blue-700" data-testid="admin-impersonate-btn">
            <Eye className="w-3.5 h-3.5" /> Accedi come
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 my-6">
        <Kpi label="STRUTTURE" value={k.properties_count} />
        <Kpi label="OSPITI" value={k.guests_count} />
        <Kpi label="PRENOTAZIONI" value={k.bookings_count} />
        <Kpi label="NOTTI TOTALI" value={k.nights_total.toLocaleString("it-IT")} />
        <Kpi label="FATTURATO" value={`€${k.revenue_total.toLocaleString("it-IT")}`} hint={`Da incassare: €${k.outstanding}`} />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-stone-200 mb-4">
        {[["overview", "Panoramica"], ["guests", `Ospiti (${guestRows.length})`], ["bookings", `Prenotazioni (${bookingRows.length})`], ["payments", `Pagamenti (${data.payments.length})`]].map(([k2, l]) => (
          <button key={k2} onClick={() => setTab(k2)}
            className={`px-4 py-2 text-sm border-b-2 ${tab === k2 ? "border-blue-700 text-blue-700 font-medium" : "border-transparent text-stone-500 hover:text-stone-900"}`}
            data-testid={`admin-tab-${k2}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <section className="grid md:grid-cols-2 gap-6">
          <div className="border border-stone-200 bg-white p-5">
            <h2 className="font-serif text-2xl mb-3">Strutture</h2>
            {data.properties.length === 0 ? <p className="text-stone-500 text-sm">Nessuna struttura.</p> : (
              <ul className="space-y-2 text-sm">
                {data.properties.map(p => (
                  <li key={p.id} className="flex justify-between border-b border-stone-100 py-1">
                    <div><div className="font-medium">{p.name}</div><div className="text-xs text-stone-500">{p.type} · {p.comune} ({p.provincia || "—"})</div></div>
                    <div className="text-xs text-stone-500">Tassa: €{p.tourist_tax_rate || 0}/n</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border border-stone-200 bg-white p-5">
            <h2 className="font-serif text-2xl mb-3">Tassa di soggiorno totale</h2>
            <p className="font-serif text-4xl text-blue-700">€{k.tourist_tax_total.toLocaleString("it-IT")}</p>
            <p className="text-xs text-stone-500 mt-2">Somma di tutte le prenotazioni con tassa calcolata.</p>
          </div>
        </section>
      )}

      {tab === "guests" && (
        <section className="border border-stone-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-client-guests">
            <thead className="bg-stone-50 text-xs tracked text-stone-500"><tr>
              <th className="text-left px-3 py-2">NOME</th><th className="text-left px-3 py-2">DOCUMENTO</th>
              <th className="text-left px-3 py-2">NAZ.</th><th className="text-left px-3 py-2">CONTATTI</th>
              <th className="text-left px-3 py-2">LINGUA</th><th></th>
            </tr></thead>
            <tbody>
              {guestRows.map(g => (
                <tr key={g.id} className="border-t border-stone-100">
                  <td className="px-3 py-2">{g.first_name} {g.last_name}{g.last_edited_by_admin && <span className="ml-2 text-[10px] text-blue-700">[admin-edit]</span>}</td>
                  <td className="px-3 py-2 text-xs">{g.document_type || "—"} · {g.document_number || "—"}</td>
                  <td className="px-3 py-2 text-xs">{g.nationality || "—"}</td>
                  <td className="px-3 py-2 text-xs">{g.email || "—"}{g.phone && <><br/>{g.phone}</>}</td>
                  <td className="px-3 py-2 text-xs">{g.language || "it"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditingGuest(g)} className="text-blue-700 underline text-xs" data-testid={`admin-edit-guest-${g.id}`}>
                      <Pencil className="w-3 h-3 inline" /> modifica
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {guestRows.length === 0 && <p className="p-6 text-stone-500 text-sm">Nessun ospite registrato.</p>}
        </section>
      )}

      {tab === "bookings" && (
        <section className="border border-stone-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-client-bookings">
            <thead className="bg-stone-50 text-xs tracked text-stone-500"><tr>
              <th className="text-left px-3 py-2">CHECK-IN</th><th className="text-left px-3 py-2">CHECK-OUT</th>
              <th className="text-right px-3 py-2">N. OSP.</th><th className="text-right px-3 py-2">TOTALE</th>
              <th className="text-right px-3 py-2">PAGATO</th><th className="text-left px-3 py-2">STATO</th><th></th>
            </tr></thead>
            <tbody>
              {bookingRows.map(b => (
                <tr key={b.id} className="border-t border-stone-100">
                  <td className="px-3 py-2 whitespace-nowrap">{b.check_in}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{b.check_out}</td>
                  <td className="px-3 py-2 text-right">{b.num_guests}</td>
                  <td className="px-3 py-2 text-right">€{(b.total_price || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">€{(b.paid_amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-stone-100 rounded-sm">{b.status}</span>{b.last_edited_by_admin && <span className="ml-2 text-[10px] text-blue-700">[admin-edit]</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditingBooking(b)} className="text-blue-700 underline text-xs" data-testid={`admin-edit-booking-${b.id}`}>
                      <Pencil className="w-3 h-3 inline" /> modifica
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {bookingRows.length === 0 && <p className="p-6 text-stone-500 text-sm">Nessuna prenotazione.</p>}
        </section>
      )}

      {tab === "payments" && (
        <section className="border border-stone-200 bg-white">
          {data.payments.length === 0 ? <p className="p-6 text-stone-500 text-sm">Nessun pagamento registrato.</p> :
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs tracked text-stone-500"><tr>
                <th className="text-left px-3 py-2">DATA</th><th className="text-right px-3 py-2">IMPORTO</th>
                <th className="text-left px-3 py-2">STATO</th><th className="text-left px-3 py-2">SESSION</th>
              </tr></thead>
              <tbody>
                {data.payments.map(p => (
                  <tr key={p.session_id || p.created_at} className="border-t border-stone-100">
                    <td className="px-3 py-2">{p.created_at && new Date(p.created_at).toLocaleString("it-IT")}</td>
                    <td className="px-3 py-2 text-right">€{(p.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2"><span className="text-xs">{p.payment_status || p.status}</span></td>
                    <td className="px-3 py-2 text-xs font-mono text-stone-500">{(p.session_id || "").slice(0, 20)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </section>
      )}

      {/* Modali */}
      {editingUser && <EditUserModal user={u} onClose={() => setEditingUser(false)} onSaved={() => { setEditingUser(false); load(); }} />}
      {editingGuest && <EditGuestModal guest={editingGuest} onClose={() => setEditingGuest(null)} onSaved={() => { setEditingGuest(null); load(); }} />}
      {editingBooking && <EditBookingModal booking={editingBooking} onClose={() => setEditingBooking(null)} onSaved={() => { setEditingBooking(null); load(); }} />}

      {showImpersonate && (
        <Modal title={`Accedi come ${u.email}`} onClose={() => setShowImpersonate(false)}>
          <form onSubmit={impersonate} className="space-y-3">
            <p className="text-sm text-stone-700">Verrai loggato come questo cliente per 4 ore. L'azione e il motivo vengono registrati nell'audit log.</p>
            <textarea value={reason} onChange={e => setReason(e.target.value)} required minLength={4}
              placeholder="Motivo (es. 'Cliente ha aperto ticket #1234')"
              className="w-full border border-stone-300 px-3 py-2 text-sm" rows={2}
              data-testid="admin-impersonate-reason" />
            <div className="flex gap-2">
              <button type="submit" className="bg-stone-900 text-stone-50 px-4 py-2 text-sm" data-testid="admin-impersonate-confirm">Conferma</button>
              <button type="button" onClick={() => setShowImpersonate(false)} className="text-stone-600 px-3 py-2 text-sm">Annulla</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ---------- Editing modals ----------
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-6 rounded-sm relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 hover:bg-stone-100"><X className="w-4 h-4" /></button>
        <h2 className="font-serif text-2xl mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs tracked text-stone-500 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

const inp = "w-full border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700";

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ full_name: user.full_name || "", email: user.email || "", phone: user.phone || "" });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/clients/${user.id}`, { reason, fields: form });
      toast.success("Anagrafica cliente aggiornata · registrata nell'audit log");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Modifica anagrafica cliente" onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="admin-edit-user-form">
        <div className="grid grid-cols-2 gap-3">
          <FormRow label="Nome completo"><input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} className={inp} data-testid="edit-user-name" /></FormRow>
          <FormRow label="Email"><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inp} required /></FormRow>
          <FormRow label="Telefono"><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={inp} /></FormRow>
        </div>
        <FormRow label="Motivo della modifica (obbligatorio per audit)">
          <textarea value={reason} onChange={e => setReason(e.target.value)} required minLength={3} rows={2}
            placeholder="Es. 'Correzione email errata su richiesta cliente — ticket #234'"
            className={inp} data-testid="edit-user-reason" />
        </FormRow>
        <div className="flex gap-2"><button type="submit" disabled={busy} className="bg-stone-900 text-stone-50 px-5 py-2.5 text-sm" data-testid="edit-user-save">{busy ? "Salvo…" : "Salva modifiche"}</button><button type="button" onClick={onClose} className="text-stone-600 px-3 py-2 text-sm">Annulla</button></div>
      </form>
    </Modal>
  );
}

function EditGuestModal({ guest, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: guest.first_name || "", last_name: guest.last_name || "",
    document_type: guest.document_type || "", document_number: guest.document_number || "",
    nationality: guest.nationality || "", birth_date: guest.birth_date || "", birth_place: guest.birth_place || "",
    issue_date: guest.issue_date || "", expiry_date: guest.expiry_date || "", issuing_authority: guest.issuing_authority || "",
    email: guest.email || "", phone: guest.phone || "", language: guest.language || "it",
  });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put(`/admin/guests/${guest.id}`, { reason, fields: form });
      toast.success("Anagrafica ospite aggiornata · registrata nell'audit log");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Modifica anagrafica ospite" onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="admin-edit-guest-form">
        <div className="grid grid-cols-2 gap-3">
          <FormRow label="Nome"><input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className={inp} required data-testid="edit-guest-first" /></FormRow>
          <FormRow label="Cognome"><input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className={inp} required data-testid="edit-guest-last" /></FormRow>
          <FormRow label="Tipo documento">
            <select value={form.document_type} onChange={e => setForm(f => ({ ...f, document_type: e.target.value }))} className={inp}>
              <option value="">—</option>
              <option value="carta_identita">Carta d'identità</option>
              <option value="passaporto">Passaporto</option>
              <option value="patente">Patente</option>
            </select>
          </FormRow>
          <FormRow label="Numero documento"><input value={form.document_number} onChange={e => setForm(f => ({ ...f, document_number: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Nazionalità (ISO3)"><input value={form.nationality} onChange={e => setForm(f => ({ ...f, nationality: e.target.value.toUpperCase() }))} className={inp} maxLength={3} /></FormRow>
          <FormRow label="Data nascita"><input type="date" value={form.birth_date} onChange={e => setForm(f => ({ ...f, birth_date: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Luogo nascita"><input value={form.birth_place} onChange={e => setForm(f => ({ ...f, birth_place: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Rilasciato il"><input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Scadenza"><input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Autorità rilascio"><input value={form.issuing_authority} onChange={e => setForm(f => ({ ...f, issuing_authority: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Email"><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Telefono"><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Lingua">
            <select value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))} className={inp}>
              <option value="it">Italiano</option><option value="en">English</option><option value="fr">Français</option><option value="de">Deutsch</option><option value="zh">中文</option>
            </select>
          </FormRow>
        </div>
        <FormRow label="Motivo della modifica (obbligatorio per audit)">
          <textarea value={reason} onChange={e => setReason(e.target.value)} required minLength={3} rows={2}
            placeholder="Es. 'Correzione errore di battitura cognome'"
            className={inp} data-testid="edit-guest-reason" />
        </FormRow>
        <div className="flex gap-2"><button type="submit" disabled={busy} className="bg-stone-900 text-stone-50 px-5 py-2.5 text-sm" data-testid="edit-guest-save">{busy ? "Salvo…" : "Salva modifiche"}</button><button type="button" onClick={onClose} className="text-stone-600 px-3 py-2 text-sm">Annulla</button></div>
      </form>
    </Modal>
  );
}

function EditBookingModal({ booking, onClose, onSaved }) {
  const [form, setForm] = useState({
    check_in: booking.check_in || "", check_out: booking.check_out || "",
    num_guests: booking.num_guests || 1, exempt_count: booking.exempt_count || 0,
    total_price: booking.total_price || 0, paid_amount: booking.paid_amount || 0,
    payment_method: booking.payment_method || "", payment_status: booking.payment_status || "non_pagato",
    status: booking.status || "confirmed", notes: booking.notes || "",
    tourist_tax_amount: booking.tourist_tax_amount || 0,
  });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const fields = { ...form,
        num_guests: Number(form.num_guests), exempt_count: Number(form.exempt_count),
        total_price: Number(form.total_price), paid_amount: Number(form.paid_amount),
        tourist_tax_amount: Number(form.tourist_tax_amount),
      };
      await api.put(`/admin/bookings/${booking.id}`, { reason, fields });
      toast.success("Prenotazione aggiornata · registrata nell'audit log");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Modifica prenotazione" onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="admin-edit-booking-form">
        <div className="grid grid-cols-2 gap-3">
          <FormRow label="Check-in"><input type="date" value={form.check_in} onChange={e => setForm(f => ({ ...f, check_in: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Check-out"><input type="date" value={form.check_out} onChange={e => setForm(f => ({ ...f, check_out: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="N. ospiti"><input type="number" min="1" value={form.num_guests} onChange={e => setForm(f => ({ ...f, num_guests: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Esenti tassa"><input type="number" min="0" value={form.exempt_count} onChange={e => setForm(f => ({ ...f, exempt_count: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Totale €"><input type="number" step="0.01" value={form.total_price} onChange={e => setForm(f => ({ ...f, total_price: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Pagato €"><input type="number" step="0.01" value={form.paid_amount} onChange={e => setForm(f => ({ ...f, paid_amount: e.target.value }))} className={inp} /></FormRow>
          <FormRow label="Metodo pagamento">
            <select value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))} className={inp}>
              <option value="">—</option><option value="contanti">Contanti</option><option value="carta">Carta</option>
              <option value="pos">POS</option><option value="bonifico">Bonifico</option><option value="booking_com">Booking.com</option>
              <option value="airbnb">Airbnb</option><option value="expedia">Expedia</option><option value="voucher">Voucher</option><option value="altro">Altro</option>
            </select>
          </FormRow>
          <FormRow label="Stato pagamento">
            <select value={form.payment_status} onChange={e => setForm(f => ({ ...f, payment_status: e.target.value }))} className={inp}>
              <option value="non_pagato">Non pagato</option><option value="pagato">Pagato</option>
              <option value="parziale">Parziale</option><option value="rimborsato">Rimborsato</option>
            </select>
          </FormRow>
          <FormRow label="Stato prenotazione">
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={inp}>
              <option value="confirmed">Confermata</option><option value="checked_in">Check-in</option>
              <option value="checked_out">Check-out</option><option value="cancelled">Cancellata</option>
            </select>
          </FormRow>
          <FormRow label="Tassa soggiorno (€)"><input type="number" step="0.01" value={form.tourist_tax_amount} onChange={e => setForm(f => ({ ...f, tourist_tax_amount: e.target.value }))} className={inp} /></FormRow>
        </div>
        <FormRow label="Note"><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className={inp} /></FormRow>
        <FormRow label="Motivo della modifica (obbligatorio per audit)">
          <textarea value={reason} onChange={e => setReason(e.target.value)} required minLength={3} rows={2}
            placeholder="Es. 'Correzione data check-out comunicata dal cliente via email'"
            className={inp} data-testid="edit-booking-reason" />
        </FormRow>
        <div className="flex gap-2"><button type="submit" disabled={busy} className="bg-stone-900 text-stone-50 px-5 py-2.5 text-sm" data-testid="edit-booking-save">{busy ? "Salvo…" : "Salva modifiche"}</button><button type="button" onClick={onClose} className="text-stone-600 px-3 py-2 text-sm">Annulla</button></div>
      </form>
    </Modal>
  );
}

// ===================================================================
// ALERTS — Vista proattiva per intervenire sui clienti a rischio (iter 26)
// ===================================================================
function Alerts() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/alerts").then(r => setData(r.data)).catch(err => toast.error(err?.response?.data?.detail || "Errore"));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-stone-500">…</p>;
  const totalCount = (data.tulps_pending?.count || 0) + (data.churn_risk?.count || 0) + (data.gdpr_gap?.count || 0);

  return (
    <div data-testid="admin-alerts">
      <p className="text-xs tracked text-blue-700 mb-2">VISTA PROATTIVA</p>
      <h1 className="font-serif text-4xl mb-2">Allerte</h1>
      <p className="text-stone-600 mb-8">{totalCount === 0 ? "Nessuna allerta attiva. Tutto sotto controllo." : `${totalCount} host richiedono attenzione. Chiamali prima che diventino problemi.`}</p>

      <AlertSection block={data.tulps_pending} severity="critical" testid="alert-tulps"
        renderExtra={r => <span className="text-xs text-red-700 font-medium">{r.pending_count} schedine pending</span>} />
      <AlertSection block={data.churn_risk} severity="warning" testid="alert-churn"
        renderExtra={r => <span className="text-xs text-blue-700">{r.days_since_signup}° giorno · 0 prenotazioni</span>} />
      <AlertSection block={data.gdpr_gap} severity="notice" testid="alert-gdpr"
        renderExtra={r => <span className="text-xs text-yellow-700">{r.without_notice_count} senza informativa</span>} />

      <button onClick={load} className="mt-4 text-xs underline text-stone-500 hover:text-blue-700">Aggiorna allerte</button>
    </div>
  );
}

function AlertSection({ block, severity, testid, renderExtra }) {
  if (!block) return null;
  const SEVERITY_STYLES = {
    critical: { cls: "border-red-300 bg-red-50",     iconCls: "text-red-700",    dot: "🔴" },
    warning:  { cls: "border-sky-300 bg-blue-50", iconCls: "text-blue-700", dot: "🟠" },
    notice:   { cls: "border-yellow-300 bg-yellow-50", iconCls: "text-yellow-700", dot: "🟡" },
  };
  const { cls, iconCls, dot } = SEVERITY_STYLES[severity] || SEVERITY_STYLES.notice;
  return (
    <section className={`border-2 ${cls} p-5 mb-5 rounded-sm`} data-testid={testid}>
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle className={`w-6 h-6 flex-shrink-0 ${iconCls}`} />
        <div className="flex-1">
          <h2 className="font-serif text-2xl">{dot} {block.label} <span className="text-stone-500 text-base font-sans">· {block.count}</span></h2>
          <p className="text-sm text-stone-700 mt-1">{block.description}</p>
        </div>
      </div>
      {block.count === 0 ? (
        <p className="text-stone-500 text-sm italic pl-9">Nessuna allerta in questa categoria. ✓</p>
      ) : (
        <ul className="pl-9 space-y-1.5">
          {block.rows.map(r => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 text-sm bg-white/70 px-3 py-2 border border-white">
              <Link to={`/admin/users/${r.id}`} className="font-medium hover:text-blue-700" data-testid={`alert-row-${r.id}`}>
                {r.full_name || r.email}
              </Link>
              <span className="text-xs font-mono text-stone-500">{r.email}</span>
              <span className="ml-auto">{renderExtra(r)}</span>
              <Link to={`/admin/users/${r.id}`} className="text-blue-700 underline text-xs">apri →</Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ===================================================================
// FINANCE — Movimenti & fatturazione (iter 25)
// ===================================================================
function Finance() {
  const [data, setData] = useState({ rows: [], count: 0, amount_paid_total: 0 });
  const [from_, setFrom] = useState(""); const [to, setTo] = useState(""); const [status, setStatus] = useState("");
  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (from_) params.set("from_", from_);
    if (to) params.set("to", to);
    if (status) params.set("status", status);
    const r = await api.get(`/admin/finance?${params.toString()}`);
    setData(r.data);
  }, [from_, to, status]);
  useEffect(() => { load().catch(err => toast.error(err?.response?.data?.detail || "Errore")); }, [load]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (from_) params.set("from_", from_);
    if (to) params.set("to", to);
    const res = await fetch(`${API}/admin/finance/export.csv?${params.toString()}`, { credentials: "include" });
    if (!res.ok) { toast.error("Export fallito"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `xenia-finance.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-finance">
      <h1 className="font-serif text-4xl mb-2">Finanza</h1>
      <p className="text-stone-600 mb-6">Movimenti pagamenti Stripe in entrata. Filtra per periodo e stato. Export CSV per il commercialista.</p>

      <div className="grid sm:grid-cols-5 gap-3 mb-6">
        <FormRow label="Dal"><input type="date" value={from_} onChange={e => setFrom(e.target.value)} className={inp} data-testid="finance-from" /></FormRow>
        <FormRow label="Al"><input type="date" value={to} onChange={e => setTo(e.target.value)} className={inp} data-testid="finance-to" /></FormRow>
        <FormRow label="Stato">
          <select value={status} onChange={e => setStatus(e.target.value)} className={inp} data-testid="finance-status">
            <option value="">Tutti</option><option value="paid">Pagato</option><option value="succeeded">Succeeded</option><option value="failed">Fallito</option><option value="pending">In corso</option>
          </select>
        </FormRow>
        <div className="flex items-end">
          <button onClick={exportCsv} className="inline-flex items-center gap-1.5 border border-stone-300 px-3 py-2 text-sm hover:bg-stone-100" data-testid="finance-export">
            <Download className="w-3.5 h-3.5" /> Esporta CSV
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Kpi label="MOVIMENTI" value={data.count} />
        <Kpi label="INCASSATO" value={`€${data.amount_paid_total.toLocaleString("it-IT")}`} />
      </div>

      <div className="border border-stone-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="finance-table">
          <thead className="bg-stone-50 text-xs tracked text-stone-500"><tr>
            <th className="text-left px-3 py-2">DATA</th><th className="text-left px-3 py-2">CLIENTE</th>
            <th className="text-right px-3 py-2">IMPORTO</th><th className="text-left px-3 py-2">STATO</th>
            <th className="text-left px-3 py-2">SESSION</th>
          </tr></thead>
          <tbody>
            {data.rows.map(p => (
              <tr key={(p.session_id || "") + (p.created_at || "")} className="border-t border-stone-100">
                <td className="px-3 py-2">{p.created_at ? new Date(p.created_at).toLocaleString("it-IT") : "—"}</td>
                <td className="px-3 py-2"><div>{p._customer?.full_name || p._customer?.email || "—"}</div><div className="text-xs text-stone-400 font-mono">{p._customer?.email}</div></td>
                <td className="px-3 py-2 text-right font-medium">€{(p.amount || 0).toFixed(2)}</td>
                <td className="px-3 py-2"><span className="text-xs">{p.payment_status || p.status}</span></td>
                <td className="px-3 py-2 text-xs font-mono text-stone-500">{(p.session_id || "").slice(0, 20)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.rows.length === 0 && <p className="p-6 text-stone-500 text-sm">Nessun movimento nel periodo selezionato.</p>}
      </div>
    </div>
  );
}

// ===================================================================
// Stats overview
// ===================================================================
function StatsOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/stats/overview").then(r => setD(r.data)).catch(err => toast.error(err?.response?.data?.detail || "Errore")); }, []);
  if (!d) return <p className="text-stone-500">…</p>;
  return (
    <div data-testid="admin-stats">
      <h1 className="font-serif text-4xl mb-2">Statistiche aggregate</h1>
      <p className="text-stone-600 mb-6">Dati anonimi — nessuna informazione personale ospite mostrata. <em>Per esigenze GDPR il proprietario non accede ai dati identificabili degli ospiti dei propri clienti.</em></p>

      <div className="grid sm:grid-cols-4 gap-4 mb-10">
        <Kpi label="PRENOTAZIONI TOTALI" value={d.totals.bookings.toLocaleString("it-IT")} />
        <Kpi label="OSPITI TOTALI" value={d.totals.guests.toLocaleString("it-IT")} />
        <Kpi label="STRUTTURE" value={d.totals.properties.toLocaleString("it-IT")} />
        <Kpi label="CAMERE" value={d.totals.rooms.toLocaleString("it-IT")} />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <section className="border border-stone-200 bg-white p-5">
          <h2 className="font-serif text-2xl mb-3">Top nazionalità ospiti</h2>
          <ul className="space-y-1 text-sm" data-testid="admin-nationalities">
            {d.nationalities_top.map(n => (
              <li key={n.nationality} className="flex justify-between">
                <span>{n.nationality}</span>
                <span className="text-stone-500">{n.guests}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="border border-stone-200 bg-white p-5">
          <h2 className="font-serif text-2xl mb-3">Tipologia strutture</h2>
          <ul className="space-y-1 text-sm" data-testid="admin-property-types">
            {d.property_types.map(t => (
              <li key={t.type} className="flex justify-between">
                <span>{t.type}</span>
                <span className="text-stone-500">{t.count}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="border border-stone-200 bg-white p-5">
        <h2 className="font-serif text-2xl mb-3">Adozione funzionalità (ultimi 30 giorni)</h2>
        <div className="grid sm:grid-cols-3 gap-4 text-sm" data-testid="admin-feature-usage">
          {Object.entries(d.feature_usage_30d).map(([k, v]) => (
            <div key={k} className="border border-stone-100 bg-stone-50 p-3">
              <div className="text-xs tracked text-stone-500 mb-1">{k.replace(/_/g, " ").toUpperCase()}</div>
              <div className="font-serif text-2xl">{v}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}


// ===================================================================
// Pannello Sicurezza — eventi, blocchi attivi, tentativi in finestra
// ===================================================================
const EVENT_LABELS = {
  login_failed: { label: "Login fallito", tone: "amber" },
  login_blocked: { label: "Login BLOCCATO", tone: "red" },
  login_success: { label: "Login riuscito", tone: "emerald" },
  two_fa_failed: { label: "Codice 2FA errato", tone: "amber" },
  two_fa_blocked: { label: "2FA BLOCCATO", tone: "red" },
  two_fa_verified: { label: "2FA verificato", tone: "emerald" },
  two_fa_enrolled: { label: "2FA attivato", tone: "blue" },
  two_fa_reset: { label: "2FA resettato", tone: "orange" },
  admin_impersonate: { label: "Admin ha impersonato un host", tone: "orange" },
  password_changed: { label: "Password cambiata", tone: "blue" },
  password_reset_requested: { label: "Richiesta reset password", tone: "blue" },
  signup: { label: "Nuova iscrizione", tone: "emerald" },
};

const TONE_CLASS = {
  red: "bg-red-100 text-red-800 border-red-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  orange: "bg-orange-100 text-orange-800 border-orange-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  stone: "bg-stone-100 text-stone-700 border-stone-200",
};

function SecurityPanel() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/security").then(r => setData(r.data)).catch(err => toast.error(err?.response?.data?.detail || "Errore"));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-stone-500">…</p>;

  const c24 = data.counters_24h || {};
  const c7d = data.counters_7d || {};
  const kpiTiles = [
    { k: "login_failed",   label: "Login falliti" },
    { k: "login_blocked",  label: "Login bloccati" },
    { k: "two_fa_failed",  label: "Codici 2FA errati" },
    { k: "admin_impersonate", label: "Impersonate admin" },
  ];

  return (
    <div data-testid="admin-security">
      <p className="text-xs tracked text-red-700 mb-2">PANNELLO SICUREZZA</p>
      <h1 className="font-serif text-4xl mb-2">Sicurezza</h1>
      <p className="text-stone-600 mb-8">Eventi sospetti, blocchi attivi e tentativi di accesso. Aggiornati in tempo reale.</p>

      {/* KPI 24h / 7d */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="security-kpis">
        {kpiTiles.map(t => (
          <div key={t.k} className="border border-stone-200 bg-white p-4 rounded-lg">
            <div className="text-[10px] tracked text-stone-500 uppercase">{t.label}</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="font-serif text-3xl">{c24[t.k] || 0}</div>
              <div className="text-xs text-stone-500">ultime 24h</div>
            </div>
            <div className="text-xs text-stone-400 mt-1">{c7d[t.k] || 0} negli ultimi 7 giorni</div>
          </div>
        ))}
      </div>

      {/* Blocchi attivi */}
      <section className="mb-8" data-testid="security-blocks">
        <h2 className="font-serif text-xl mb-3 flex items-center gap-2">
          <Lock className="w-4 h-4 text-red-700" /> Blocchi attivi
          <span className="text-xs text-stone-500 font-sans">({data.blocked_now?.length || 0})</span>
        </h2>
        {data.blocked_now?.length === 0 ? (
          <p className="text-stone-500 text-sm">Nessun blocco attivo. Nessuno sta tentando attacchi di indovino.</p>
        ) : (
          <div className="border border-red-200 bg-red-50/30 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-red-100/50 text-xs tracked text-red-900">
                <tr><th className="text-left px-3 py-2">Chi</th><th className="text-left px-3 py-2">Dove</th><th className="text-left px-3 py-2">Sbloccato tra</th></tr>
              </thead>
              <tbody>
                {data.blocked_now.map((b, i) => (
                  <tr key={i} className="border-t border-red-100">
                    <td className="px-3 py-2 font-mono text-xs">{b.ident}</td>
                    <td className="px-3 py-2 text-stone-600">{b.bucket === "login_ip" ? "Login (per IP)" : b.bucket === "login_email" ? "Login (per email)" : b.bucket === "twofa" ? "2FA (per admin)" : b.bucket}</td>
                    <td className="px-3 py-2 text-red-800">{Math.ceil(b.blocked_for_seconds / 60)} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Tentativi in corso */}
      <section className="mb-8" data-testid="security-attempts">
        <h2 className="font-serif text-xl mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700" /> Tentativi falliti in corso
          <span className="text-xs text-stone-500 font-sans">({data.attempts_in_window?.length || 0})</span>
        </h2>
        {data.attempts_in_window?.length === 0 ? (
          <p className="text-stone-500 text-sm">Nessun tentativo fallito nella finestra di 15 minuti.</p>
        ) : (
          <div className="border border-amber-200 bg-amber-50/30 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-amber-100/50 text-xs tracked text-amber-900">
                <tr><th className="text-left px-3 py-2">Chi</th><th className="text-left px-3 py-2">Dove</th><th className="text-left px-3 py-2">Tentativi</th></tr>
              </thead>
              <tbody>
                {data.attempts_in_window.map((a, i) => (
                  <tr key={i} className="border-t border-amber-100">
                    <td className="px-3 py-2 font-mono text-xs">{a.ident}</td>
                    <td className="px-3 py-2 text-stone-600">{a.bucket === "login_ip" ? "Login (per IP)" : a.bucket === "login_email" ? "Login (per email)" : a.bucket === "twofa" ? "2FA (per admin)" : a.bucket}</td>
                    <td className="px-3 py-2 text-amber-800">{a.attempts_in_window} / 5</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Eventi recenti */}
      <section data-testid="security-events">
        <h2 className="font-serif text-xl mb-3 flex items-center gap-2">
          <FileSearch className="w-4 h-4 text-stone-700" /> Eventi recenti
          <span className="text-xs text-stone-500 font-sans">(ultimi {data.recent_events?.length || 0})</span>
        </h2>
        <div className="border border-stone-200 bg-white rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs tracked text-stone-500">
              <tr>
                <th className="text-left px-3 py-2">Quando</th>
                <th className="text-left px-3 py-2">Evento</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_events || []).map((e, i) => {
                const meta = EVENT_LABELS[e.event_type] || { label: e.event_type, tone: "stone" };
                return (
                  <tr key={e.id || i} className="border-t border-stone-100 hover:bg-stone-50/50">
                    <td className="px-3 py-2 font-mono text-[11px] text-stone-500">{(e.at || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 text-[10px] rounded-full border ${TONE_CLASS[meta.tone] || TONE_CLASS.stone}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-stone-700">{e.email || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-stone-500">{e.ip || "—"}</td>
                  </tr>
                );
              })}
              {(!data.recent_events || data.recent_events.length === 0) && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-stone-500">Nessun evento registrato.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <button onClick={load} className="mt-4 text-xs underline text-stone-500 hover:text-blue-700" data-testid="security-refresh">
        Aggiorna
      </button>
    </div>
  );
}


// ===================================================================
// Audit log
// ===================================================================
function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit?limit=200").then(r => setRows(r.data)).catch(err => toast.error(err?.response?.data?.detail || "Errore")); }, []);
  return (
    <div data-testid="admin-audit">
      <h1 className="font-serif text-4xl mb-2">Audit log</h1>
      <p className="text-stone-600 mb-6">Ogni azione admin viene registrata qui (200 ultime).</p>
      <div className="border border-stone-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-stone-50 text-stone-500 tracked">
            <tr>
              <th className="text-left px-4 py-2">QUANDO</th>
              <th className="text-left px-4 py-2">ADMIN</th>
              <th className="text-left px-4 py-2">AZIONE</th>
              <th className="text-left px-4 py-2">IP</th>
              <th className="text-left px-4 py-2">DETTAGLI</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.created_at + "-" + i} className="border-t border-stone-100">
                <td className="px-4 py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                <td className="px-4 py-2 font-mono">{r.admin_email || "—"}</td>
                <td className="px-4 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded-sm border ${
                    r.action.includes("fail") ? "bg-red-50 text-red-700 border-red-200" :
                    r.action.includes("impersonate") ? "bg-blue-50 text-blue-700 border-blue-200" :
                    "bg-stone-50 text-stone-600 border-stone-200"
                  }`}>{r.action}</span>
                </td>
                <td className="px-4 py-2 font-mono">{r.ip || "—"}</td>
                <td className="px-4 py-2 text-stone-500">{JSON.stringify(r.details || {})}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminLayout;
